import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import {
  RolodexAdvancedFilterOperator,
  RolodexEntryType,
  RolodexFilterOp,
} from 'types'

export function useOffPageConnectorDocuments(
  searchText: string | string[] | undefined,
  siteID: string
) {
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()
  const loadOptions = useMemo(() => {
    const searchTexts = (
      Array.isArray(searchText) ? searchText : [searchText]
    ).filter((text): text is string => !!text)
    if (isPipelineConfigScopeLoading || searchTexts.length === 0)
      return undefined

    // One OR clause per (field, text): Rolodex's REGEX op matches regex
    // metacharacters literally, so alternation like `(a|b)` finds nothing.
    const escaped = searchTexts.map((text) =>
      text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    )
    const filters = escaped.flatMap((value) =>
      (['title', 'drawing_number', 'document_title'] as const).map((key) => ({
        field: 'data',
        key,
        op: RolodexFilterOp.REGEX,
        value,
      }))
    )

    return {
      type: [RolodexEntryType.SYSTEM_DOCUMENT],
      tags: getPipelineConfigScopedTags({ site: siteID }),
      advanced_filter: {
        operator: RolodexAdvancedFilterOperator.OR,
        filters,
      },
      limit: 1000000,
    }
  }, [
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    searchText,
    siteID,
  ])

  const { data, isLoading, error } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(loadOptions)

  return {
    systemDocumentEntries: data ?? [],
    isLoading,
    error,
  }
}
